import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import { canAccessWorkspace, getAccessDeniedMessage } from "../../util/accessControl";
import useAuth from "../../hooks/useAuth";

const AdminLayout = ({ children }) => {
  const { user } = useAuth();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem("sidebarCollapsed");
    return stored ? JSON.parse(stored) : false;
  });
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const toggleCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  const toggleMobile = useCallback(() => {
    setMobileSidebarOpen((prev) => !prev);
  }, []);

  const allowed = canAccessWorkspace(user);

  if (!allowed) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-10 font-inter">
        <div className="mx-auto max-w-xl rounded-3xl border border-rose-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-600">
            <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="mt-4 text-xl font-semibold text-slate-950">Access denied</h1>
          <p className="mt-2 text-sm text-slate-600">{getAccessDeniedMessage()}</p>
          <div className="mt-6 flex justify-center gap-3">
            <Link to="/home" className="inline-flex items-center rounded-xl bg-[#096dea] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0959bb]">
              Go home
            </Link>
            <Link to="/login" className="inline-flex items-center rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
              Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-inter">
      <Topbar />
      <div className="flex">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={toggleCollapsed}
          mobileOpen={mobileSidebarOpen}
          onMobileToggle={toggleMobile}
        />
        <main className="relative min-w-0 flex-1 px-4 py-4 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={toggleMobile}
            className="md:hidden absolute top-0 left-0 z-30 flex h-8 w-8 items-center justify-center rounded-lg bg-white border border-slate-200 text-slate-600 shadow-sm hover:bg-slate-50 transition"
          >
            {mobileSidebarOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
          <div className="mx-auto max-w-7xl pt-6 md:pt-0">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;